import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, ShieldCheck, BarChart3, Settings2, X, Check } from 'lucide-react';

const DEFAULT_PREFS = { essential: true, analytics: true, preferences: true };

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [manage, setManage] = useState(false);
  const [prefs, setPrefs] = useState(DEFAULT_PREFS);

  useEffect(() => {
    const stored = localStorage.getItem('salihi_cookies');
    if (!stored) {
      const t = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(t);
    }
  }, []);

  const save = (choice) => {
    localStorage.setItem('salihi_cookies', JSON.stringify({ choice, prefs, savedAt: new Date().toISOString() }));
    setVisible(false);
  };
  const toggle = (key) => setPrefs((p) => ({ ...p, [key]: !p[key] }));
  if (!visible) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-[60] px-3 sm:px-4 pb-3 sm:pb-5">
      <div className="mx-auto max-w-[860px] bg-[#0a1b35]/97 backdrop-blur-2xl text-white rounded-3xl shadow-2xl border border-[#e8b863]/25 overflow-hidden">
        <div className="flex items-start justify-between gap-4 px-5 sm:px-8 pt-6 sm:pt-7 pb-5">
          <div className="flex items-start gap-3 sm:gap-4">
            <span className="flex-shrink-0 w-11 h-11 rounded-xl bg-[#e8b863]/18 border border-[#e8b863]/45 flex items-center justify-center">
              <Cookie className="w-5 h-5 text-[#e8b863]" />
            </span>
            <div>
              <p className="text-[11px] text-[#e8b863] tracking-[0.22em] uppercase font-semibold">Your privacy</p>
              <h3 className="font-serif text-white text-[20px] sm:text-[24px] leading-tight mt-1">We value your privacy</h3>
            </div>
          </div>
          <button onClick={() => save('dismissed')} aria-label="Dismiss" className="text-white/65 hover:text-white transition-colors mt-1">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 sm:px-8 pb-2 text-white/78 text-[13.5px] sm:text-[14px] leading-relaxed space-y-3">
          <p>
            Salihi Consulting Services uses cookies to make this website function
            correctly, understand how visitors engage with our content, and remember
            your preferences across visits. You can accept all cookies, reject
            non-essential ones, or choose exactly which categories to allow.
          </p>
          {!manage && (
            <p>
              For full details on how we collect, store, and process information,
              please review our{' '}
              <Link to="/cookies-policy" className="text-[#e8b863] underline underline-offset-2 hover:text-[#f0c97a]">Cookies Policy</Link>{' '}and{' '}
              <Link to="/privacy-policy" className="text-[#e8b863] underline underline-offset-2 hover:text-[#f0c97a]">Privacy Policy</Link>.
            </p>
          )}
        </div>

        {manage && (
          <div className="px-5 sm:px-8 py-5 space-y-3">
            <Category icon={ShieldCheck} title="Strictly necessary" desc="Required for core site functionality such as navigation, security, and form submission. These cannot be switched off." enabled locked />
            <Category icon={BarChart3} title="Analytics" desc="Help us understand which pages are useful and how visitors navigate the site, so we can improve the experience." enabled={prefs.analytics} onToggle={() => toggle('analytics')} />
            <Category icon={Settings2} title="Preferences" desc="Remember language, theme, and other personalisation choices so the site feels familiar on your next visit." enabled={prefs.preferences} onToggle={() => toggle('preferences')} />
          </div>
        )}

        <div className="px-5 sm:px-8 py-5 border-t border-white/10 flex flex-wrap items-center justify-between gap-3 bg-white/[0.02]">
          <button onClick={() => setManage((m) => !m)} className="text-[13px] sm:text-[13.5px] text-white/78 hover:text-[#e8b863] transition-colors inline-flex items-center gap-2 font-medium">
            <Settings2 className="w-4 h-4" />
            {manage ? 'Hide preferences' : 'Manage preferences'}
          </button>
          <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto justify-end">
            <button onClick={() => { setPrefs({ essential: true, analytics: false, preferences: false }); save('rejected'); }} className="px-4 sm:px-5 py-2.5 text-[12.5px] sm:text-[13px] font-medium text-white/85 hover:text-white border border-white/18 rounded-full hover:border-white/35 transition-colors">
              Reject non-essential
            </button>
            {manage && (
              <button onClick={() => save('custom')} className="px-4 sm:px-5 py-2.5 text-[12.5px] sm:text-[13px] font-semibold text-white bg-white/10 hover:bg-white/15 border border-white/22 rounded-full transition-colors">
                Save preferences
              </button>
            )}
            <button onClick={() => { setPrefs({ essential: true, analytics: true, preferences: true }); save('accepted'); }} className="px-5 sm:px-6 py-2.5 text-[12.5px] sm:text-[13px] font-semibold text-[#0a1b35] bg-gradient-to-r from-[#d49d3f] to-[#b8842a] hover:from-[#e8b863] hover:to-[#d49d3f] rounded-full shadow-lg shadow-[#d49d3f]/20 transition-all">
              Accept all cookies
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Category({ icon: Icon, title, desc, enabled, onToggle, locked }) {
  return (
    <div className="flex items-start gap-4 p-4 rounded-2xl bg-white/[0.04] border border-white/10">
      <span className="flex-shrink-0 w-10 h-10 rounded-lg bg-[#e8b863]/14 border border-[#e8b863]/35 flex items-center justify-center mt-0.5">
        <Icon className="w-4 h-4 text-[#e8b863]" />
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-3">
          <p className="text-white font-semibold text-[14.5px]">{title}</p>
          {locked ? (
            <span className="text-[10.5px] text-[#e8b863] tracking-wider uppercase font-semibold">Always active</span>
          ) : (
            <button onClick={onToggle} role="switch" aria-checked={enabled} className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors ${enabled ? 'bg-[#e8b863]' : 'bg-white/18'}`}>
              <span className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform mt-0.5 ${enabled ? 'translate-x-[22px]' : 'translate-x-0.5'}`}>
                {enabled && <Check className="w-3 h-3 text-[#0a1b35] m-1" strokeWidth={3} />}
              </span>
            </button>
          )}
        </div>
        <p className="text-white/68 text-[12.5px] sm:text-[13px] leading-relaxed mt-1.5">{desc}</p>
      </div>
    </div>
  );
}
